import apiClient from '@/lib/api-client'
import type { Project } from '@/shared/types/entities'
import type { ApiResponse } from '@/shared/types/api'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'

export interface CreateProjectDto {
  name: string
  clientId: string
  color: string
  teamIds?: string[]
}

export interface UpdateProjectDto extends Partial<CreateProjectDto> {}

interface ProjectsApiResponse {
  data: {
    data: {
      projects: Project[]
    }
  }
}

function getWorkspaceId(): string {
  const { selectedWorkspace } = useWorkspaceStore.getState()
  if (!selectedWorkspace) {
    throw new Error('No workspace selected')
  }
  return selectedWorkspace.id
}

export const projectService = {
  async getProjects(includeArchived = false, search?: string): Promise<Project[]> {
    const workspaceId = getWorkspaceId()
    const params: Record<string, string> = { includeArchived: includeArchived.toString() }
    if (search) params.search = search
    const response = await apiClient.get<ProjectsApiResponse>(`/workspaces/${workspaceId}/projects`, { params })
    // Handle nested response: response.data.data.data.projects
    const projects = response.data?.data?.data?.projects
    return Array.isArray(projects) ? projects : []
  },
  
  async getProjectById(id: string): Promise<Project> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.get<ApiResponse<Project>>(`/workspaces/${workspaceId}/projects/${id}`)
    return response.data.data
  },
  
  async createProject(data: CreateProjectDto): Promise<Project> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<ApiResponse<Project>>(`/workspaces/${workspaceId}/projects`, data)
    return response.data.data
  },
  
  async updateProject(id: string, data: UpdateProjectDto): Promise<Project> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.patch<ApiResponse<Project>>(`/workspaces/${workspaceId}/projects/${id}`, data)
    return response.data.data
  },
  
  async archiveProject(id: string): Promise<void> {
    const workspaceId = getWorkspaceId()
    await apiClient.post(`/workspaces/${workspaceId}/projects/${id}/archive`)
  },
  
  async restoreProject(id: string): Promise<Project> {
    const workspaceId = getWorkspaceId()
    const response = await apiClient.post<ApiResponse<Project>>(`/workspaces/${workspaceId}/projects/${id}/restore`)
    return response.data.data
  },
}

export default projectService
